// Phase 2 / Step D — coach-app App shell.
//
// Coach-app is a separate Vite SPA mounted at the `coach.<host>` hostname.
// It re-uses the EXACT same source files for individual coach pages
// from learner-app via the `@scenario-os/...` alias defined in
// vite.config.js, so there is NO source duplication.
//
// URL pattern note:
//   The coach-page source files internally call `navigate('/coach/X')`
//   and use `<Link to="/coach/X">`. To keep this hard-cutover migration
//   zero-risk we KEEP the `/coach/*` route prefix here and serve them
//   from `coach.<host>/coach/...`. URLs read as a bit redundant
//   (`https://coach.dev-1.example.com/coach/dashboard`) — that is
//   intentional during the migration. A follow-up codemod will strip
//   the `/coach/` prefix from navigate()/Link to= calls and we can
//   then drop the prefix from these routes too. Keeping the prefix
//   means we touch 0 source files in pages/coach/*.

import React, { useEffect, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from '@scenario-os/context/AuthContext';
import { BrandingProvider, useBranding } from '@scenario-os/context/BrandingContext';
import { ViewportOrientationProvider } from '@scenario-os/context/ViewportOrientationContext';
import AccessDenied from '@scenario-os/components/AccessDenied';
import { OverlayHost } from '@shared/utils/overlays';

class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[CoachApp ErrorBoundary]', error, info?.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, maxWidth: 600, margin: '80px auto', fontFamily: 'system-ui, sans-serif' }}>
          <h1 style={{ color: '#dc2626', fontSize: 22 }}>Something went wrong</h1>
          <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.6 }}>
            The coach app encountered an unexpected error. Please try refreshing the page.
          </p>
          <pre style={{
            background: '#1e293b', color: '#fbbf24', padding: 16, borderRadius: 8,
            fontSize: 12, lineHeight: 1.5, overflow: 'auto', maxHeight: 300,
          }}>
            {String(this.state.error)}
          </pre>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: 16, background: '#3b82f6', color: '#fff', border: 'none',
              padding: '10px 20px', borderRadius: 6, fontSize: 14, cursor: 'pointer',
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

import AuthPage from '@scenario-os/pages/AuthPage';
import CoachDashboardPage from '@scenario-os/pages/coach/CoachDashboardPage';
import CurriculumBuilderPage from '@scenario-os/pages/coach/CurriculumBuilderPage';
import TutorialBuilderPage from '@scenario-os/pages/coach/TutorialBuilderPage';
import LearnerManagementPage from '@scenario-os/pages/coach/LearnerManagementPage';
import PlayLibraryPage from '@scenario-os/pages/coach/PlayLibraryPage';
import CoverageLibraryPage from '@scenario-os/pages/coach/CoverageLibraryPage';
import PositionsPage from '@scenario-os/pages/coach/PositionsPage';
import SceneLibraryPage from '@scenario-os/pages/coach/SceneLibraryPage';
import SceneDesignerPage from '@scenario-os/pages/coach/SceneDesignerPage';
import VideoSceneDesignerPage from '@scenario-os/pages/coach/VideoSceneDesignerPage';
import SlideDeckSceneDesignerPage from '@scenario-os/pages/coach/SlideDeckSceneDesignerPage';
import GenericSceneEditorPage from '@scenario-os/pages/coach/GenericSceneEditorPage';
// Phase 13 P4 — eager-import the bootstrap to ensure tools are
// registered before the first coach navigation hits a designer
// page. The DesignerShellPage wrapper also calls
// bootstrapDesignerToolbox() in a useEffect so this is belt-and-
// suspenders, but doing it once here means the very first
// Shell-V2 render has the registry warm.
//
// Phase 13 P5 polish — domain-specific tools register at the
// app-boot site, not inside the framework. Coach-app bundles
// every domain its tenant supports; the registry's predicate
// pipeline (which checks `requiresDomain('football')` against
// the active scene's domain id) hides tools that don't apply.
import { bootstrapDesignerToolbox } from '@scenario-os/designer';
import footballDomainTools from '@scenario-os/designer/domain_tools/football/index';
import PlaybooksPage from '@scenario-os/pages/coach/PlaybooksPage';
import RostersPage from '@scenario-os/pages/coach/RostersPage';
import BrandingAdminPage from '@scenario-os/pages/coach/BrandingAdminPage';
import TenantAdminPage from '@scenario-os/pages/coach/TenantAdminPage';
// Phase 13 P5 — platform-admin Audit tab. Mounted under
// `/admin/audit` so it's accessible only via direct nav from the
// platform_admin sidebar (the route guard at the API rejects
// non-admins regardless).
import AdminAuditPage from '@scenario-os/pages/coach/AdminAuditPage';
import AdminUsersPage from '@scenario-os/pages/coach/AdminUsersPage';
import AdminOrgsPage from '@scenario-os/pages/coach/AdminOrgsPage';
import AdminBillingPage from '@scenario-os/pages/coach/AdminBillingPage';
import AuthoringChatPanel from '@scenario-os/pages/coach/AuthoringChatPanel';
import ChatMonitorPage from '@scenario-os/pages/coach/ChatMonitorPage';
import AnnouncementsPage from '@scenario-os/pages/coach/AnnouncementsPage';
import ModerationQueue from '@scenario-os/components/discussions/ModerationQueue';
import SubmissionQueue from '@scenario-os/components/rubrics/SubmissionQueue';
import CoachAnalyticsPage from '@scenario-os/pages/coach/CoachAnalyticsPage';
import CoachAnalyticsDashboard from '@scenario-os/pages/coach/CoachAnalyticsDashboard';
import CoachUsageReportsPage from '@scenario-os/pages/coach/CoachUsageReportsPage';
import WorkspaceBuilderPage from '@scenario-os/pages/coach/WorkspaceBuilderPage';
// Phase 8 — Adaptive diagnostic pages
import LearnerAdaptiveDetailPage from '@scenario-os/pages/coach/LearnerAdaptiveDetailPage';
import DomainHealthPage from '@scenario-os/pages/coach/DomainHealthPage';
import AdaptiveDiagnosticsPage from '@scenario-os/pages/coach/AdaptiveDiagnosticsPage';
import ItemBankManagerPage from '@scenario-os/pages/coach/ItemBankManagerPage';
import ReviewCardLibraryPage from '@scenario-os/pages/coach/ReviewCardLibraryPage';
import DomainSettingsPage from '@scenario-os/pages/coach/DomainSettingsPage';
import CapstoneSubmissionsPage from '@scenario-os/pages/coach/CapstoneSubmissionsPage';
import ConfigStoreAdminPage from '@scenario-os/pages/coach/ConfigStoreAdminPage';
import AdminRolesPage from '@scenario-os/pages/coach/AdminRolesPage';
import FlashcardManagementPage from '@scenario-os/pages/coach/FlashcardManagementPage';
import AILHealthPage from '@scenario-os/pages/coach/AILHealthPage';

function mustSetPassword(learner) {
  return Boolean(learner?.password_change_required);
}

// Phase 13 P4 — boot the Designer Toolbox registry exactly once at
// app load. The bootstrap is idempotent (subsequent calls no-op) so
// even if a designer page also calls it, this is the canonical
// initialization point for the coach SPA. The flag-branch in each
// legacy designer page picks the rendered shell.
//
// Phase 13 P5 polish — `extraTools` is where every domain pack
// supplied by this app's tenant injects its DesignerTool list.
// Today coach-app bundles football; multi-domain tenants (AI
// orchestrator, etc.) will append their own tool arrays to this
// spread. Domain packs that ship NO designer tools simply omit
// themselves; the framework still works.
bootstrapDesignerToolbox({
  extraTools: [
    ...footballDomainTools,
  ],
});

function RequireCoach({ children }) {
  const { isAuthenticated, isCoach, learner } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (mustSetPassword(learner)) return <Navigate to="/login" replace />;
  if (!isCoach) {
    return (
      <AccessDenied
        reason="The coach app is for authoring users. Sign in at the learner URL to access lessons."
        onBack={() => window.history.back()}
      />
    );
  }
  return children;
}

function RequireAdmin({ children }) {
  const { isAuthenticated, isAdmin, isCoach, learner } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (mustSetPassword(learner)) return <Navigate to="/login" replace />;
  if (!isCoach) {
    return (
      <AccessDenied
        reason="The coach app is for authoring users."
        onBack={() => window.history.back()}
      />
    );
  }
  if (!isAdmin) {
    return (
      <AccessDenied
        reason="This page is restricted to administrators."
        onBack={() => window.history.back()}
      />
    );
  }
  return children;
}

/** Platform-only routes (AIL Health, Audit, Roles, Config Store, Branding, Tenants). */
function RequirePlatformAdmin({ children, accessKey }) {
  const {
    isAuthenticated, isCoach, isPlatformAdmin, hasUiAccess, learner,
  } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (mustSetPassword(learner)) return <Navigate to="/login" replace />;
  if (!isCoach) {
    return (
      <AccessDenied
        reason="The coach app is for authoring users."
        onBack={() => window.history.back()}
      />
    );
  }
  const ui = accessKey ? hasUiAccess?.(accessKey) : null;
  if (!isPlatformAdmin && ui !== true) {
    return (
      <AccessDenied
        reason="This page is restricted to platform administrators."
        onBack={() => window.history.back()}
      />
    );
  }
  return children;
}

function RequireOwnerOrAdmin({ children }) {
  // Billing: owner + platform_admin (matches BILLING_ROLES). Administrators
  // with ui_access.billing=view_only get read-only entry via hasUiAccess.
  const { isAuthenticated, isCoach, isOwner, isPlatformAdmin, hasUiAccess, learner } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (mustSetPassword(learner)) return <Navigate to="/login" replace />;
  if (!isCoach) {
    return (
      <AccessDenied
        reason="The coach app is for authoring users."
        onBack={() => window.history.back()}
      />
    );
  }
  // hasUiAccess returns true for billing:true and billing:"view_only".
  // While ui_access is still loading (null), allow owner / platform_admin /
  // administrator so we don't flash Access Restricted incorrectly.
  const billingUi = hasUiAccess?.('billing');
  const role = learner?.role;
  const canBilling =
    isOwner ||
    isPlatformAdmin ||
    billingUi === true ||
    (billingUi === null && (role === 'owner' || role === 'administrator' || role === 'platform_admin'));
  if (!canBilling) {
    return (
      <AccessDenied
        reason="Billing is restricted to domain owners and platform administrators. Domain administrators have view-only billing when enabled on their role."
        onBack={() => window.history.back()}
      />
    );
  }
  return children;
}

function RequireAuthoringDomain({ children }) {
  const { canAuthorDomain } = useAuth();
  const params = useParams();
  const domain = params.domain || params.domainId;
  if (!domain) return children;
  if (!canAuthorDomain(domain)) {
    return (
      <AccessDenied
        domainId={domain}
        reason="You are not entitled to author content for this domain. Contact an admin to request access."
        onBack={() => window.history.back()}
      />
    );
  }
  return children;
}

function PublicRoute({ children }) {
  const { isAuthenticated, isCoach, learner } = useAuth();
  if (isAuthenticated && !mustSetPassword(learner)) {
    return <Navigate to={isCoach ? '/coach' : '/login'} replace />;
  }
  return children;
}

function BrandingAuthSync() {
  const { learner } = useAuth();
  const { activeClientId, setClientId } = useBranding();
  useEffect(() => {
    const profileClientId = learner?.client_id || null;
    if (profileClientId && profileClientId !== activeClientId) {
      setClientId(profileClientId);
    }
  }, [learner, activeClientId, setClientId]);
  return null;
}

function RootRedirect() {
  const { isAuthenticated, isCoach, learner } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (mustSetPassword(learner)) return <Navigate to="/login" replace />;
  if (!isCoach) {
    return (
      <AccessDenied
        reason="The coach app is for authoring users. Sign in at the learner URL to access lessons."
      />
    );
  }
  return <Navigate to="/coach" replace />;
}

export default function App() {
  return (
    <AppErrorBoundary>
    <AuthProvider>
      <BrandingProvider>
        <ViewportOrientationProvider>
          <BrandingAuthSync />
        <BrowserRouter basename="/">
          <Routes>
            <Route path="/login" element={<PublicRoute><AuthPage /></PublicRoute>} />
            <Route path="/" element={<RootRedirect />} />

            {/* Coach routes — same /coach/* paths as the legacy combined
                app so internal navigate() / <Link to=> calls in the
                imported page source do not need to change. */}
            <Route path="/coach" element={<RequireCoach><CoachDashboardPage /></RequireCoach>} />
            <Route path="/coach/curriculum/:domain" element={<RequireCoach><RequireAuthoringDomain><CurriculumBuilderPage /></RequireAuthoringDomain></RequireCoach>} />
            {/* Lesson Library removed — redirect to Content Studio */}
            <Route path="/coach/tutorials" element={<Navigate to="/coach/content-studio" replace />} />
            <Route path="/coach/tutorials/:tutorialId" element={<Navigate to="/coach/content-studio" replace />} />
            <Route path="/coach/learners" element={<RequireCoach><LearnerManagementPage /></RequireCoach>} />
            <Route path="/coach/plays/:domain" element={<RequireCoach><RequireAuthoringDomain><PlayLibraryPage /></RequireAuthoringDomain></RequireCoach>} />
            <Route path="/coach/coverages/:domain" element={<RequireCoach><RequireAuthoringDomain><CoverageLibraryPage /></RequireAuthoringDomain></RequireCoach>} />
            <Route path="/coach/positions/:domain" element={<RequireCoach><RequireAuthoringDomain><PositionsPage /></RequireAuthoringDomain></RequireCoach>} />
            <Route path="/coach/scene-library/:domain" element={<RequireCoach><RequireAuthoringDomain><SceneLibraryPage /></RequireAuthoringDomain></RequireCoach>} />
            <Route path="/coach/scenes/:sceneId" element={<RequireCoach><SceneDesignerPage /></RequireCoach>} />
            <Route path="/coach/video-scenes/:sceneId" element={<RequireCoach><VideoSceneDesignerPage /></RequireCoach>} />
            <Route path="/coach/slide-deck-scenes/:sceneId" element={<RequireCoach><SlideDeckSceneDesignerPage /></RequireCoach>} />
            <Route path="/coach/flashcard-scenes/:sceneId" element={<RequireCoach><GenericSceneEditorPage /></RequireCoach>} />
            <Route path="/coach/scene-data/:sceneId" element={<RequireCoach><GenericSceneEditorPage /></RequireCoach>} />
            <Route path="/coach/admin/branding" element={<RequirePlatformAdmin accessKey="branding"><BrandingAdminPage /></RequirePlatformAdmin>} />
            <Route path="/coach/admin/tenants" element={<RequirePlatformAdmin accessKey="tenants"><TenantAdminPage /></RequirePlatformAdmin>} />
            <Route path="/coach/admin/audit"   element={<RequirePlatformAdmin accessKey="audit"><AdminAuditPage /></RequirePlatformAdmin>} />
            {/* Org / Users / Billing — admin-shared (scoped server-side) */}
            <Route path="/coach/admin/users"         element={<RequireAdmin><AdminUsersPage /></RequireAdmin>} />
            <Route path="/coach/admin/organizations" element={<RequireAdmin><AdminOrgsPage /></RequireAdmin>} />
            <Route path="/coach/admin/config-store"  element={<RequirePlatformAdmin accessKey="config_store"><ConfigStoreAdminPage /></RequirePlatformAdmin>} />
            <Route path="/coach/admin/roles"         element={<RequirePlatformAdmin accessKey="role_management"><AdminRolesPage /></RequirePlatformAdmin>} />
            <Route path="/coach/admin/billing"       element={<RequireOwnerOrAdmin><AdminBillingPage /></RequireOwnerOrAdmin>} />
            <Route path="/coach/playbooks/:domain" element={<RequireCoach><RequireAuthoringDomain><PlaybooksPage /></RequireAuthoringDomain></RequireCoach>} />
            <Route path="/coach/rosters" element={<RequireCoach><RostersPage /></RequireCoach>} />
            <Route path="/coach/chats" element={<RequireCoach><ChatMonitorPage /></RequireCoach>} />
            <Route path="/coach/content-studio" element={<RequireCoach><AuthoringChatPanel /></RequireCoach>} />
            <Route path="/coach/announcements" element={<RequireCoach><AnnouncementsPage /></RequireCoach>} />
            <Route path="/coach/moderation" element={<RequireCoach><ModerationQueue /></RequireCoach>} />
            <Route path="/coach/submissions" element={<RequireCoach><SubmissionQueue /></RequireCoach>} />
            <Route path="/coach/analytics" element={<RequireCoach><CoachAnalyticsPage /></RequireCoach>} />
            <Route path="/coach/analytics-dashboard" element={<RequireCoach><CoachAnalyticsDashboard /></RequireCoach>} />
            <Route path="/coach/usage" element={<RequireCoach><CoachUsageReportsPage /></RequireCoach>} />
            <Route path="/coach/workspace-builder" element={<RequireAdmin><WorkspaceBuilderPage /></RequireAdmin>} />
            <Route path="/coach/workspace/:domain/item-bank" element={<RequireCoach><RequireAuthoringDomain><ItemBankManagerPage /></RequireAuthoringDomain></RequireCoach>} />
            <Route path="/coach/workspace/:domain/review-cards" element={<RequireCoach><RequireAuthoringDomain><ReviewCardLibraryPage /></RequireAuthoringDomain></RequireCoach>} />
            <Route path="/coach/domain-settings/:domain" element={<RequireCoach><RequireAuthoringDomain><DomainSettingsPage /></RequireAuthoringDomain></RequireCoach>} />
            {/* Capstone Approvals merged into All Submissions */}
            <Route path="/coach/capstone-reviews" element={<Navigate to="/coach/submissions" replace />} />
            <Route path="/coach/flashcards" element={<RequireCoach><FlashcardManagementPage /></RequireCoach>} />

            {/* Phase 8 — Adaptive diagnostic routes */}
            <Route path="/coach/learners/:learnerId/adaptive" element={<RequireCoach><LearnerAdaptiveDetailPage /></RequireCoach>} />
            <Route path="/coach/workspace/:domain/health" element={<RequireCoach><RequireAuthoringDomain><DomainHealthPage /></RequireAuthoringDomain></RequireCoach>} />
            <Route path="/coach/admin/adaptive-diagnostics" element={<RequireAdmin><AdaptiveDiagnosticsPage /></RequireAdmin>} />
            <Route path="/coach/platform/ail-health" element={<RequirePlatformAdmin accessKey="ail_health"><AILHealthPage /></RequirePlatformAdmin>} />

            <Route path="*" element={<RootRedirect />} />
          </Routes>
        </BrowserRouter>
        {/* Phase 2 / Step E — global toast + confirm host. Anywhere in
            the tree can call toast.success(...) or await confirm({...}). */}
        <OverlayHost />
        </ViewportOrientationProvider>
      </BrandingProvider>
    </AuthProvider>
    </AppErrorBoundary>
  );
}
